import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogTitle, Skeleton, cn } from '@mlm/ui';
import {
  deleteAssistantMemoryNote,
  listAssistantMemory,
  type AssistantMemoryNote,
} from '../../lib/assistant-api';

/**
 * "What the assistant remembers about me" — the notes it saved for itself
 * (`save_memory`), each individually removable. Persona intake answers are
 * filtered out server-side because they're edited in the About-you card, so
 * nothing here is shown twice.
 *
 * Clearing everything stays on the sidebar's own confirm flow (it also wipes
 * the intake answers, which this list deliberately doesn't cover).
 */
export function MemoryPanel({
  open,
  onOpenChange,
  onClearAll,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClearAll: () => void;
}) {
  const qc = useQueryClient();
  const notesQ = useQuery({
    queryKey: ['assistant', 'memory'],
    queryFn: listAssistantMemory,
    enabled: open,
  });

  const forget = useMutation({
    mutationFn: (id: string) => deleteAssistantMemoryNote(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant', 'memory'] }),
    meta: { successMessage: 'Forgotten.', errorMessage: 'Could not delete that note.' },
  });

  const notes = notesQ.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogTitle>What the assistant remembers</DialogTitle>
        <p className="text-xs text-muted-foreground">
          Notes it saved while you chatted. Delete anything that&apos;s wrong or out of date — it
          won&apos;t be used again.
        </p>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {notesQ.isLoading ? (
            <>
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </>
          ) : notes.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing saved yet. As you chat, the assistant notes things worth remembering.
            </p>
          ) : (
            notes.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                busy={forget.isPending}
                onForget={() => forget.mutate(n.id)}
              />
            ))
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <button
            type="button"
            className="rounded text-left text-[11px] text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => {
              onOpenChange(false);
              onClearAll();
            }}
          >
            Clear everything, including my profile answers
          </button>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** `health_conditions` → `Health conditions`. Keys are snake_case by contract. */
function prettyKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function NoteRow({
  note,
  busy,
  onForget,
}: {
  note: AssistantMemoryNote;
  busy: boolean;
  onForget: () => void;
}) {
  // Values are capped at 500 chars server-side; long ones still deserve a clamp.
  const [expanded, setExpanded] = useState(false);
  const long = note.value.length > 140;

  return (
    <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {prettyKey(note.key)}
        </p>
        <p className={cn('break-words text-sm', !expanded && long && 'line-clamp-2')}>
          {note.value}
        </p>
        {long && (
          <button
            type="button"
            className="mt-0.5 rounded text-[11px] text-o underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
      <button
        type="button"
        title="Forget this"
        aria-label={`Forget: ${prettyKey(note.key)}`}
        disabled={busy}
        onClick={onForget}
        className="flex-none rounded p-1 text-muted-foreground/70 transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
